/**
 * Converts bytes to a human-readable string.
 * @param bytes - The number of bytes.
 * @returns A string representing the size in a human-readable format.
 */
export function formatFileSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Reads a file and returns its content as a string.
 * @param file - The file to read.
 * @returns A promise that resolves with the file content as a string.
 */
export function readFileAsString(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

/**
 * Reads a file and returns its content as an ArrayBuffer.
 * @param file - The file to read.
 * @returns A promise that resolves with the file content as an ArrayBuffer.
 */
export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Reads a file and returns its content as a Data URL.
 * @param file - The file to read.
 * @returns A promise that resolves with the file content as a Data URL.
 */
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Converts a file to a Blob.
 * @param file - The file to convert.
 * @returns A promise that resolves with the file content as a Blob.
 */
export function fileToBlob(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Blob([reader.result]));
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Compares the similarity of two files by their content.
 * @param file1 - The first file to compare.
 * @param file2 - The second file to compare.
 * @returns A promise that resolves with a boolean indicating whether the files are similar.
 */
export function compareFiles(file1: File, file2: File): Promise<boolean> {
  return Promise.all([
    readFileAsArrayBuffer(file1),
    readFileAsArrayBuffer(file2)
  ]).then(([buffer1, buffer2]) => {
    if (buffer1.byteLength !== buffer2.byteLength) return false;
    const view1 = new Uint8Array(buffer1);
    const view2 = new Uint8Array(buffer2);
    for (let i = 0; i < view1.length; i++) {
      if (view1[i] !== view2[i]) return false;
    }
    return true;
  });
}

/**
 * Imports a CSV file and returns its content as an array of objects.
 * @param file - The CSV file to import.
 * @returns A promise that resolves with the file content as an array of objects.
 */
export function importCSV(file: File): Promise<any[]> {
  return readFileAsString(file).then((csvString) => {
    const rows = csvString.split('\n');
    const headers = rows[0].split(',');
    return rows.slice(1).map((row) => {
      const values = row.split(',');
      return headers.reduce((obj, header, index) => {
        obj[header] = values[index];
        return obj;
      }, {});
    });
  });
}

/**
 * Exports an array of objects to a CSV file and triggers a download.
 * @param data - The data to export.
 * @param filename - The name of the file to save.
 */
export function exportCSV(data: any[], filename: string): void {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map((row) => headers.map((header) => row[header]).join(','))
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
